import { animate, motion, useMotionTemplate, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import React, { useEffect, useId } from 'react';
import { cn } from '../../lib/utils';
import parts from '../../assets/brand/laptura-wordmark-parts.json';

export const DRIVE_HOLD_SECONDS = 0.5;
export const DRIVE_TRAVEL_SECONDS = 3;
export const DRIVE_SECONDS = DRIVE_HOLD_SECONDS + DRIVE_TRAVEL_SECONDS;

const [boxX, boxY, , boxH] = parts.viewBox.split(' ').map(Number);
// Inner face of the L stem (`l14.986` in the rule). The car sits on the
// foot just past that, like the favicon — not behind the stem.
export const START_CAR_X = 18;
export const END_CAR_X = parts.car.x;
export const TRAVEL = END_CAR_X - START_CAR_X;
export const START_OFFSET = START_CAR_X - END_CAR_X;
// Crop includes the whole car. The rule is clipped separately so the
// baseline meets the rear wing and cannot run past the nose. 12 clears
// the visible L corner without reaching the hole in the rear deck —
// that hole is where a sliver of L used to show through the car.
const NOSE_PAD = 2;
export const RULE_TUCK = 12;
// getBBox() of `parts.rule`. Object-bounding-box clip is a 0–1 fraction of
// this, so CSS clip-path cannot remap the crop into pixels.
export const RULE_WIDTH = 379.222;
// Same-color halo as the page so the car stays outlined against the
// baseline while it drives, matching the parked mark. Light-field kit
// frames use 3; the app is dark, so the live stroke is the wider one.
export const CAR_HALO = 3;
export const CAR_HALO_ON_DARK = 4;

export function viewBoxWidthAt(p) {
  return START_CAR_X + p * TRAVEL + parts.car.width + NOSE_PAD - boxX;
}

export const DRIVE_START_ASPECT = viewBoxWidthAt(0) / boxH;

export function ruleClipRightAt(p) {
  return START_CAR_X + p * TRAVEL + RULE_TUCK;
}

export function ruleClipFracAt(p) {
  return ruleClipRightAt(p) / RULE_WIDTH;
}

/**
 * Progress (0–1) at which the car's rear reaches this letter's left edge.
 * Same number drives both the car's x and the letter's fade, so they cannot
 * drift apart even if the easing curve changes.
 */
export function letterAppearAt(letterX) {
  return Math.max(0, Math.min(1, (letterX - START_CAR_X) / TRAVEL));
}

function Letter({ d, appearAt, progress, still }) {
  const opacity = useTransform(progress, [appearAt, Math.min(1, appearAt + 0.12)], [0, 1]);
  if (still) return <path d={d} />;
  return <motion.path d={d} style={{ opacity }} />;
}

/**
 * The Laptura wordmark assembling itself: the car sets off from the foot of
 * the L, the frame grows with it, and each letter fades in as the car
 * passes underneath.
 *
 * Geometry comes from laptura-wordmark-parts.json, which the brand build
 * script splits out of the same export that produces the static wordmark, so
 * the two cannot drift apart.
 */
export function LapturaDriveIn({ className, progress: progressOverride }) {
  const still = useReducedMotion();
  const clipId = useId().replace(/:/g, '');
  const frozen = still || progressOverride != null;
  const progress = useMotionValue(frozen ? (progressOverride ?? 1) : 0);
  const carX = useTransform(progress, [0, 1], [START_OFFSET, 0]);
  const vbW = useTransform(progress, viewBoxWidthAt);
  const viewBox = useMotionTemplate`${boxX} ${boxY} ${vbW} ${boxH}`;
  const clipFrac = useTransform(progress, ruleClipFracAt);
  const clipD = useMotionTemplate`M0 0H${clipFrac}V1H0Z`;

  useEffect(() => {
    if (progressOverride != null) {
      progress.set(progressOverride);
      return undefined;
    }
    if (still) return undefined;
    let travel;
    // Sit on the L first so the takeoff isn't a snap, then ease into the run.
    const hold = animate(progress, 0, { duration: DRIVE_HOLD_SECONDS });
    hold.then(() => {
      travel = animate(progress, 1, {
        duration: DRIVE_TRAVEL_SECONDS,
        ease: [0.48, 0.0, 0.16, 1],
      });
    });
    return () => {
      hold.stop();
      travel?.stop();
    };
  }, [progress, still, progressOverride]);

  return (
    <motion.svg
      viewBox={viewBox}
      className={cn('overflow-hidden', className)}
      fill="currentColor"
      role="img"
      aria-label="Laptura"
    >
      <defs>
        <clipPath id={clipId} clipPathUnits="objectBoundingBox">
          <motion.path d={clipD} />
        </clipPath>
      </defs>
      <path d={parts.rule} clipPath={`url(#${clipId})`} />
      <motion.path
        d={parts.car.d}
        data-testid="drive-in-halo"
        fill="rgb(var(--lap-bg))"
        stroke="rgb(var(--lap-bg))"
        strokeWidth={CAR_HALO_ON_DARK}
        strokeLinejoin="round"
        style={{ x: still ? 0 : carX }}
      />
      {parts.letters.map((letter) => (
        <Letter
          key={letter.x}
          d={letter.d}
          appearAt={letterAppearAt(letter.x)}
          progress={progress}
          still={still}
        />
      ))}
      <motion.path d={parts.car.d} style={{ x: still ? 0 : carX }} />
    </motion.svg>
  );
}
